import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, DollarSign, Gift, Heart, ShieldCheck, Flame,
  TrendingUp, Trophy, Star, CheckCircle, XCircle, AlertTriangle,
  Loader2, BarChart3, Target, Award, Zap, CheckCheck, ChevronDown,
  AlertCircle, Compass, Eye, Sparkles, Pencil,
} from "lucide-react";
import { getCategoryColor } from "@/lib/categoryColors";
import { trackPageView } from "@/lib/track";

const StatCard = ({ icon: Icon, label, value, color, sub, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-2xl shadow-md p-4 flex flex-col items-center text-center ${onClick ? "cursor-pointer hover:shadow-lg active:scale-95 transition-all" : ""}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <span className="text-2xl font-bold text-slate-800">{value}</span>
    <span className="text-xs text-slate-500 mt-0.5">{label}</span>
    {sub && <span className="text-[10px] text-slate-400 mt-0.5">{sub}</span>}
    {onClick && <span className="text-[9px] text-sky-500 font-medium mt-1">Tap to view</span>}
  </div>
);

const ProgressBar = ({ pct, color = "bg-sky-500" }) => (
  <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className={`h-full rounded-full ${color}`} />
  </div>
);

/* #7 Activity heatmap — 45 days, each column = 1 day, numbered at bottom */
const ActivityHeatmap = ({ heatmap }) => {
  const maxCount = Math.max(...heatmap.map(d => d.count), 1);
  const getColor = (count) => {
    if (count === 0) return "bg-slate-100";
    const intensity = count / maxCount;
    if (intensity > 0.75) return "bg-emerald-500";
    if (intensity > 0.5) return "bg-emerald-400";
    if (intensity > 0.25) return "bg-emerald-300";
    return "bg-emerald-200";
  };

  return (
    <div className="overflow-x-auto">
      <div className="flex gap-[4px] min-w-max">
        {heatmap.map((day, i) => (
          <div key={i} className="flex flex-col items-center gap-[3px]">
            <div className={`w-4 h-4 rounded-full ${getColor(day.count)}`} title={`${day.date}: ${day.count} actions`} />
            <span className="text-[7px] text-slate-400 font-medium leading-none">{day.day}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* #8 Category radar chart — uses category-specific colors */
const CategoryRadar = ({ data }) => (
  <div className="space-y-2.5">
    {data.map((d, i) => {
      const cc = getCategoryColor(d.category);
      return (
        <div key={i}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-full ${cc.bg}`} />
              <span className="text-xs text-slate-600">{d.category}</span>
            </div>
            <span className="text-xs font-bold text-slate-700">{d.value} actions</span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${d.pct}%` }} transition={{ duration: 0.6, delay: i * 0.1 }} className={`h-full rounded-full ${cc.bg}`} />
          </div>
        </div>
      );
    })}
  </div>
);

const AccordionSection = ({ id, icon: Icon, title, count, color, isOpen, onToggle, children }) => (
  <div className="rounded-2xl overflow-hidden shadow-md" data-testid={`accordion-${id}`}>
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-3 px-4 py-3.5 ${color} text-white transition-all`}
      data-testid={`accordion-btn-${id}`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm font-bold flex-1 text-left">{title}</span>
      {count != null && <span className="text-xs font-semibold bg-white/20 px-2.5 py-0.5 rounded-full">{count}</span>}
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <ChevronDown className="w-5 h-5" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className="bg-white overflow-hidden"
        >
          <div className="p-4">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const RoiDeepDiveModal = ({ isOpen, onClose, birthday_roi }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const roiYears = Object.keys(birthday_roi?.years || {}).sort().reverse();
  const activeYear = selectedYear || birthday_roi?.current_year || roiYears[0];
  const yearData = birthday_roi?.years?.[activeYear];
  const catBreakdown = yearData?.category_breakdown || {};
  const catEntries = Object.entries(catBreakdown).sort((a, b) => b[1].total - a[1].total);
  const maxCatTotal = Math.max(...catEntries.map(([, v]) => v.total), 1);

  // All-time totals for the year bar chart
  const yearTotals = roiYears.map(y => ({ year: y, total: birthday_roi?.years?.[y]?.total || 0 }));
  const maxYearTotal = Math.max(...yearTotals.map(y => y.total), 1);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="roi-deepdive-overlay"
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="roi-deepdive-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-amber-400 to-yellow-500 p-5 text-white relative">
            <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition" data-testid="roi-deepdive-close">
              <XCircle className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-6 h-6" />
              <span className="font-bold text-lg">Birthday ROI Deep-Dive</span>
            </div>
            <p className="text-4xl font-bold">${yearData?.total?.toFixed(2) || "0.00"}</p>
            <p className="text-sm text-white/80 mt-1">{yearData?.count || 0} deals in {activeYear}</p>
          </div>

          {/* Year Tabs */}
          {roiYears.length > 0 && (
            <div className="flex gap-2 px-5 py-3 border-b border-slate-100 overflow-x-auto">
              {roiYears.map(y => (
                <button
                  key={y}
                  onClick={() => setSelectedYear(y)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition whitespace-nowrap ${y === activeYear ? "bg-amber-500 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                  data-testid={`roi-dd-year-${y}`}
                >
                  {y}
                </button>
              ))}
            </div>
          )}

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Year-over-Year Bar Chart */}
            {yearTotals.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Savings by Year</h3>
                <div className="space-y-2">
                  {yearTotals.map(({ year, total }) => (
                    <div key={year} className="flex items-center gap-3">
                      <span className={`text-xs font-semibold w-10 ${year === activeYear ? "text-amber-600" : "text-slate-400"}`}>{year}</span>
                      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(total / maxYearTotal) * 100}%` }}
                          transition={{ duration: 0.6 }}
                          className={`h-full rounded-full ${year === activeYear ? "bg-gradient-to-r from-amber-400 to-amber-500" : "bg-slate-300"}`}
                        />
                      </div>
                      <span className={`text-xs font-bold w-16 text-right ${year === activeYear ? "text-green-600" : "text-slate-500"}`}>${total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Breakdown */}
            {catEntries.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">By Category in {activeYear}</h3>
                <div className="space-y-2">
                  {catEntries.map(([cat, info]) => {
                    const cc = getCategoryColor(cat);
                    return (
                      <div key={cat} className={`${cc.light} border ${cc.border} rounded-xl p-3`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${cc.bg}`} />
                            <span className="text-xs font-medium text-slate-700">{cat}</span>
                            <span className="text-[10px] text-slate-400">{info.count} {info.count === 1 ? "deal" : "deals"}</span>
                          </div>
                          <span className="text-sm font-bold text-green-600">${info.total.toFixed(2)}</span>
                        </div>
                        <div className="w-full h-1.5 bg-white/60 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(info.total / maxCatTotal) * 100}%` }}
                            transition={{ duration: 0.5 }}
                            className={`h-full rounded-full ${cc.bg}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Full Items List */}
            {yearData?.items?.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">All Deals in {activeYear}</h3>
                <div className="space-y-1.5">
                  {yearData.items.map((item, i) => {
                    const cc = getCategoryColor(item.category);
                    return (
                      <div key={i} className={`flex items-center gap-2 text-xs ${cc.light} border ${cc.border} rounded-lg px-3 py-2`}>
                        <span className={`w-5 h-5 rounded-md ${cc.bg} flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0`}>{i + 1}</span>
                        <span className="text-slate-700 font-medium truncate flex-1">{item.business_name}</span>
                        <span className={`text-[10px] ${cc.text}`}>{item.category}</span>
                        <span className="text-green-600 font-bold">${item.value.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(!yearData?.items?.length) && (
              <div className="text-center py-8 text-slate-400">
                <Gift className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No deals claimed in {activeYear} yet</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default function UserAnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getStatus } = useUnifiedStatus();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(null);
  const [roiModalOpen, setRoiModalOpen] = useState(false);
  const [badgeExplainOpen, setBadgeExplainOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [catExplorerOpen, setCatExplorerOpen] = useState(false);
  const toggleSection = (key) => setOpenSection(openSection === key ? null : key);

  useEffect(() => {
    trackPageView("user_analytics");
    (async () => {
      try { const res = await axios.get(`${API}/user/analytics`); setData(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">Unable to load analytics</p></div>;

  const { summary, best_freebie, top_category_badge, top_category_name, top_category_count, monthly_goal, savings_comparison, level, badges, badge_milestones, verification_stats: vs, category_savings, category_radar, streak, heatmap, claimed_timeline, money_left, birthday_roi, category_explorer, confidence_insights, top_unclaimed } = data;
  const maxCatSaved = Math.max(...category_savings.map(c => c.saved), 1);
  const roiYears = Object.keys(birthday_roi?.years || {}).sort().reverse();
  const activeRoiYear = birthday_roi?.current_year || roiYears[0];
  const roiData = birthday_roi?.years?.[activeRoiYear];

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="user-analytics-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-sky-400 to-indigo-500 text-white p-4 pt-6 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition" data-testid="analytics-back-btn"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-lg font-bold">My Analytics</h1>
        </div>
        <div className="text-center">
          <p className="text-white/70 text-sm">Hey {user?.first_name},</p>
          <p className="text-5xl font-bold mt-1">${summary.total_saved.toFixed(2)}</p>
          <p className="text-white/80 text-sm mt-1">total estimated savings</p>
          {savings_comparison.multiplier > 1 && (
            <p className="text-white/90 text-xs mt-2 bg-white/15 rounded-full px-3 py-1 inline-block" data-testid="savings-comparison">
              <Zap className="w-3 h-3 inline mr-1" />You save {savings_comparison.multiplier}x more than the average user
            </p>
          )}
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-5">
        {/* Summary Grid */}
        <div className="grid grid-cols-3 gap-3" data-testid="summary-grid">
          <StatCard icon={Gift} label="Claimed" value={summary.offers_claimed} color="bg-green-500" onClick={() => navigate("/my-deals")} />
          <StatCard icon={ShieldCheck} label="Verified" value={summary.verifications} color="bg-sky-500" />
          <StatCard icon={Heart} label="Favorites" value={summary.favorites} color="bg-pink-500" onClick={() => navigate("/my-deals")} />
        </div>

        {/* #1 Best Freebie + #2 Worth It */}
        <div className="grid grid-cols-2 gap-3">
          {best_freebie && (
            <div onClick={() => navigate(`/offer/${best_freebie.business_id}`)} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 cursor-pointer hover:shadow-lg transition-shadow" data-testid="best-freebie">
              <div className="flex items-center gap-2 mb-2">
                {best_freebie.logo_url ? (
                  <img src={best_freebie.logo_url} alt="" className="w-8 h-8 rounded-lg object-contain bg-white border border-amber-100" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-amber-200 flex items-center justify-center"><Trophy className="w-4 h-4 text-amber-600" /></div>
                )}
                <p className="text-xs text-amber-500 font-medium">Best Deal</p>
              </div>
              <p className="text-sm font-bold text-amber-800 truncate">{best_freebie.name}</p>
              <p className="text-xl font-bold text-green-600 mt-0.5">${best_freebie.value.toFixed(2)}</p>
              <p className="text-[10px] text-amber-500 truncate mt-0.5">{best_freebie.title}</p>
            </div>
          )}
          {(() => {
            const signedUp = summary.offers_signed_up || 0;
            const claimedCount = summary.offers_claimed || 0;
            const completionPct = signedUp > 0 ? Math.round((claimedCount / signedUp) * 100) : null;
            return completionPct !== null ? (
              <div onClick={() => navigate("/my-deals")} className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-2xl p-4 cursor-pointer hover:shadow-lg active:scale-95 transition-all" data-testid="claim-completion">
                <CheckCheck className="w-5 h-5 text-sky-500 mb-2" />
                <p className="text-xs text-sky-600 font-medium">Claim Rate</p>
                <p className="text-xl font-bold text-sky-800 mt-0.5">{completionPct}%</p>
                <p className="text-[10px] text-sky-500 mt-0.5">{claimedCount} of {signedUp} signed-up claimed</p>
                <p className="text-[9px] text-sky-500 font-medium mt-1">Tap to view</p>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-2xl p-4" data-testid="claim-completion-empty">
                <CheckCheck className="w-5 h-5 text-sky-400 mb-2" />
                <p className="text-xs text-sky-500 font-medium">Claim Rate</p>
                <p className="text-sm text-sky-400 mt-1">Sign up for deals to track your rate</p>
              </div>
            );
          })()}
        </div>

        {top_category_badge && (
          <div
            onClick={() => setBadgeExplainOpen(!badgeExplainOpen)}
            className="bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-2xl px-5 py-3 cursor-pointer hover:shadow-md active:scale-[0.98] transition-all"
            data-testid="top-category-badge"
          >
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-sky-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-sky-800">{top_category_badge}</p>
                <p className="text-[10px] text-sky-500">Your most claimed category · Tap to learn more</p>
              </div>
              <motion.div animate={{ rotate: badgeExplainOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-4 h-4 text-sky-400" />
              </motion.div>
            </div>
            <AnimatePresence>
              {badgeExplainOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pt-3 border-t border-sky-200 text-xs text-sky-700 space-y-1.5" data-testid="badge-explanation">
                    <p>You earned this badge by claiming <strong>{top_category_count || "the most"}</strong> {top_category_name || "deals"} deals — more than any other category!</p>
                    <p className="text-[10px] text-sky-500">Keep claiming to unlock new badges and level up your deal game.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* #4 Monthly Savings Goal — Editable */}
        <div className="bg-white rounded-2xl shadow-md p-5" data-testid="monthly-goal">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-sky-500" />
              <span className="font-semibold text-slate-800">Monthly Goal</span>
            </div>
            {editingGoal ? (
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-green-600 font-bold">${monthly_goal.saved.toFixed(2)} /</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-slate-400">$</span>
                  <input
                    type="number"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    className="w-16 text-sm font-bold text-sky-700 border border-sky-300 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-sky-400"
                    min="1" max="10000" autoFocus
                    data-testid="goal-input"
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        const val = parseFloat(goalInput);
                        if (val > 0) {
                          await axios.put(`${API}/user/monthly-goal`, { goal: val });
                          setData(prev => ({ ...prev, monthly_goal: { ...prev.monthly_goal, target: val, pct: Math.min(100, Math.round((prev.monthly_goal.saved / val) * 100)) } }));
                        }
                        setEditingGoal(false);
                      }
                      if (e.key === "Escape") setEditingGoal(false);
                    }}
                  />
                  <button
                    onClick={async () => {
                      const val = parseFloat(goalInput);
                      if (val > 0) {
                        await axios.put(`${API}/user/monthly-goal`, { goal: val });
                        setData(prev => ({ ...prev, monthly_goal: { ...prev.monthly_goal, target: val, pct: Math.min(100, Math.round((prev.monthly_goal.saved / val) * 100)) } }));
                      }
                      setEditingGoal(false);
                    }}
                    className="text-xs bg-sky-500 text-white px-2 py-0.5 rounded-lg font-semibold hover:bg-sky-600 transition"
                    data-testid="goal-save-btn"
                  >Save</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setGoalInput(String(monthly_goal.target)); setEditingGoal(true); }}
                className="text-sm font-bold text-green-600 hover:text-sky-600 transition flex items-center gap-1 group"
                data-testid="goal-edit-btn"
              >
                ${monthly_goal.saved.toFixed(2)} / ${monthly_goal.target}
                <Pencil className="w-3 h-3 text-slate-300 group-hover:text-sky-500 transition" />
              </button>
            )}
          </div>
          <ProgressBar pct={monthly_goal.pct} color="bg-gradient-to-r from-sky-400 to-sky-500" />
          <p className="text-[10px] text-slate-400 mt-1.5">{monthly_goal.pct}% of your monthly savings target{!editingGoal && " · Tap amount to change goal"}</p>
        </div>

        {/* Level & Streak */}
        <div className="bg-white rounded-2xl shadow-md p-5" data-testid="level-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              <span className="font-bold text-slate-800">{level.current}</span>
              <span className="text-xs text-slate-400">{level.score} pts</span>
            </div>
            <div className="flex items-center gap-1.5 bg-orange-50 px-3 py-1 rounded-full border border-orange-200">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-bold text-orange-600">{streak.current}</span>
              <span className="text-xs text-orange-400">day streak</span>
            </div>
          </div>
          <ProgressBar pct={level.progress_pct} color="bg-gradient-to-r from-amber-400 to-amber-500" />
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-slate-400">{level.current}</span>
            <span className="text-[10px] text-slate-400">{level.next_level} ({level.next_threshold} pts)</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">{streak.active_days_30d} active days in the last 30 days</p>
        </div>

        {/* ═══════ INSIGHT WIDGETS & ACCORDIONS — Uniform 8px gap ═══════ */}
        <div className="flex flex-col gap-2">

        {/* Widget 1: Money Left on the Table — Accordion */}
        {money_left && money_left.count > 0 && (
          <AccordionSection id="money-left" icon={AlertCircle} title={`Money Left on the Table · $${money_left.total.toFixed(2)}`} count={money_left.count} color="bg-gradient-to-r from-orange-400 to-red-400" isOpen={openSection === "money-left"} onToggle={() => toggleSection("money-left")}>
            <p className="text-xs text-slate-500 mb-3">{money_left.count} signed-up offers you haven't claimed yet</p>
            <div className="space-y-1.5">
              {money_left.items.map((item, i) => {
                const cc = getCategoryColor(item.category);
                return (
                  <div key={i} onClick={() => navigate(`/offer/${item.business_id}`)} className={`flex items-center justify-between text-xs ${cc.light} rounded-lg px-3 py-2 border ${cc.border} cursor-pointer hover:shadow-sm transition`} data-testid={`money-left-item-${i}`}>
                    <span className="text-slate-700 font-medium truncate flex-1">{item.business_name}</span>
                    <span className={`text-[10px] ${cc.text} mx-2`}>{item.category}</span>
                    <span className="text-green-600 font-bold">${item.value.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
            <button onClick={() => navigate("/my-deals")} className="mt-3 text-xs font-semibold text-orange-600 hover:text-orange-800 transition" data-testid="money-left-cta">Go claim them →</button>
          </AccordionSection>
        )}

        {/* Widget 5: Top Unclaimed Deals — Accordion */}
        {top_unclaimed && top_unclaimed.length > 0 && (
          <AccordionSection id="unclaimed" icon={Sparkles} title="Deals You're Missing" count={top_unclaimed.length} color="bg-gradient-to-r from-sky-400 to-sky-500" isOpen={openSection === "unclaimed"} onToggle={() => toggleSection("unclaimed")}>
            <div className="space-y-2">
              {top_unclaimed.map((deal, i) => {
                const cc = getCategoryColor(deal.category);
                return (
                  <div key={i} onClick={() => navigate(`/offer/${deal.business_id}`)} className={`flex items-center gap-3 ${cc.light} border ${cc.border} rounded-xl p-3 cursor-pointer hover:shadow-sm transition`} data-testid={`unclaimed-deal-${i}`}>
                    <div className={`w-8 h-8 rounded-lg ${cc.bg} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-xs font-bold text-white">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{deal.business_name}</p>
                      <p className={`text-[10px] ${cc.text} font-medium`}>{deal.category}{deal.confidence ? ` · ${deal.confidence}% confidence` : ""}</p>
                    </div>
                    <span className="text-sm font-bold text-green-600">${deal.value.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </AccordionSection>
        )}

        </div>

        {/* Widget 2: Birthday ROI — clickable for deep-dive */}
        {roiData && (
          <div
            onClick={() => setRoiModalOpen(true)}
            className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-5 cursor-pointer hover:shadow-lg active:scale-[0.98] transition-all"
            data-testid="widget-birthday-roi"
          >
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-bold text-amber-800">Birthday ROI</span>
              {roiYears.length > 1 && <span className="text-[10px] bg-amber-200 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">{roiYears.length} years</span>}
            </div>
            <p className="text-3xl font-bold text-green-600">${roiData.total.toFixed(2)}</p>
            <p className="text-xs text-amber-500 mt-1">{roiData.count} deals claimed in {activeRoiYear}</p>
            <p className="text-[9px] text-amber-500 font-medium mt-2">Tap for deep-dive</p>
          </div>
        )}

        {/* Birthday ROI Deep-Dive Modal */}
        {data?.birthday_roi && (
          <RoiDeepDiveModal
            isOpen={roiModalOpen}
            onClose={() => setRoiModalOpen(false)}
            birthday_roi={data.birthday_roi}
          />
        )}

        {/* Widget 3: Category Explorer — clickable to explore */}
        {category_explorer && (
          <div
            onClick={() => setCatExplorerOpen(!catExplorerOpen)}
            className="bg-white rounded-2xl shadow-md p-5 cursor-pointer hover:shadow-lg active:scale-[0.98] transition-all"
            data-testid="widget-category-explorer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Compass className="w-5 h-5 text-teal-500" />
                <span className="text-sm font-bold text-slate-800">Category Explorer</span>
              </div>
              <motion.div animate={{ rotate: catExplorerOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </motion.div>
            </div>
            <div className="flex items-center gap-5">
              <div className="relative w-20 h-20 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#14b8a6" strokeWidth="3" strokeDasharray={`${(category_explorer.explored / Math.max(category_explorer.total, 1)) * 100} 100`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-teal-600">{category_explorer.explored}</span>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-700 font-medium">{category_explorer.explored} of {category_explorer.total} categories</p>
                <p className="text-[10px] text-slate-400 mt-1">{Math.round((category_explorer.explored / Math.max(category_explorer.total, 1)) * 100)}% explored · Tap to see all</p>
              </div>
            </div>

            <AnimatePresence>
              {catExplorerOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Explored categories */}
                  <div className="mt-4 pt-3 border-t border-slate-100">
                    <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-2">Explored ({category_explorer.explored_list?.length || 0})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(category_explorer.explored_list || []).map((cat, i) => {
                        const cc = getCategoryColor(cat);
                        return (
                          <span
                            key={i}
                            onClick={() => navigate(`/?category=${encodeURIComponent(cat)}`)}
                            className={`px-2 py-1 ${cc.light} ${cc.text} border ${cc.border} rounded-lg text-[11px] font-semibold cursor-pointer hover:shadow-sm transition flex items-center gap-1`}
                            data-testid={`explored-cat-${i}`}
                          >
                            <CheckCheck className="w-3 h-3" />{cat}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Missing categories */}
                  {category_explorer.missing_list?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Not Yet Explored ({category_explorer.missing_list.length})</p>
                      <div className="flex flex-wrap gap-1.5">
                        {category_explorer.missing_list.map((cat, i) => {
                          const cc = getCategoryColor(cat);
                          return (
                            <span
                              key={i}
                              onClick={() => navigate(`/?category=${encodeURIComponent(cat)}`)}
                              className="px-2 py-1 bg-slate-50 text-slate-500 border border-slate-200 border-dashed rounded-lg text-[11px] font-medium cursor-pointer hover:bg-slate-100 hover:border-slate-300 transition"
                              data-testid={`missing-cat-${i}`}
                            >
                              {cat}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Widget 4: Confidence Picker Score */}
        {confidence_insights?.user_avg != null && confidence_insights?.overall_avg != null && (
          <div className="bg-white rounded-2xl shadow-md p-5" data-testid="widget-confidence">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-bold text-slate-800">Confidence Picker</span>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{confidence_insights.user_avg}%</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Your avg</p>
              </div>
              <div className="text-center text-slate-300 text-xs font-medium">vs</div>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-400">{confidence_insights.overall_avg}%</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Overall avg</p>
              </div>
            </div>
            <p className="text-xs mt-3 text-slate-500">
              {confidence_insights.user_avg > confidence_insights.overall_avg
                ? `You pick winners! Your deals are ${confidence_insights.user_avg - confidence_insights.overall_avg}% more reliable than average.`
                : confidence_insights.user_avg === confidence_insights.overall_avg
                ? "Right on par with the community average."
                : `Room to grow — look for deals with higher confidence scores.`}
            </p>
          </div>
        )}

        {/* ═══════ ACCORDION SECTIONS — Uniform 8px gap ═══════ */}
        <div className="flex flex-col gap-2">

        {/* Next Achievements */}
        {badge_milestones && badge_milestones.length > 0 && (
          <AccordionSection id="milestones" icon={Star} title="Next Achievements" count={badge_milestones.length} color="bg-gradient-to-r from-amber-400 to-amber-500" isOpen={openSection === "milestones"} onToggle={() => toggleSection("milestones")}>
            <div className="space-y-3">
              {badge_milestones.map((m, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-slate-700 font-medium">{m.badge}</span>
                    <span className="text-xs text-slate-400">{m.current}/{m.target}</span>
                  </div>
                  <ProgressBar pct={m.pct} color={m.pct >= 75 ? "bg-green-500" : m.pct >= 50 ? "bg-amber-400" : "bg-sky-400"} />
                  <p className="text-[10px] text-slate-400 mt-0.5">{m.target - m.current} more to {m.description}</p>
                </div>
              ))}
            </div>
          </AccordionSection>
        )}

        {/* Badges */}
        {badges.length > 0 && (
          <AccordionSection id="badges" icon={Award} title="Badges" count={badges.length} color="bg-gradient-to-r from-amber-500 to-amber-600" isOpen={openSection === "badges"} onToggle={() => toggleSection("badges")}>
            <div className="flex flex-wrap gap-2">
              {badges.map((badge, i) => (
                <span key={i} className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-xs font-semibold text-amber-700">{badge}</span>
              ))}
            </div>
          </AccordionSection>
        )}

        {/* Verification Stats */}
        <AccordionSection id="verification" icon={ShieldCheck} title="Verification Stats" count={vs.confirmed + vs.denied + vs.changed} color="bg-gradient-to-r from-sky-400 to-sky-500" isOpen={openSection === "verification"} onToggle={() => toggleSection("verification")}>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div><div className="flex items-center justify-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-500" /><span className="text-lg font-bold text-green-600">{vs.confirmed}</span></div><p className="text-[10px] text-slate-400">Confirmed</p></div>
            <div><div className="flex items-center justify-center gap-1"><XCircle className="w-3.5 h-3.5 text-red-500" /><span className="text-lg font-bold text-red-600">{vs.denied}</span></div><p className="text-[10px] text-slate-400">Denied</p></div>
            <div><div className="flex items-center justify-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /><span className="text-lg font-bold text-amber-600">{vs.changed}</span></div><p className="text-[10px] text-slate-400">Changed</p></div>
            <div><div className="flex items-center justify-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-sky-500" /><span className="text-lg font-bold text-sky-600">{vs.accuracy_pct}%</span></div><p className="text-[10px] text-slate-400">Accuracy</p></div>
          </div>
        </AccordionSection>

        {/* Activity Heatmap */}
        {heatmap && heatmap.length > 0 && (
          <AccordionSection id="heatmap" icon={Flame} title="Activity (Last 45 Days)" count={streak.active_days_30d} color="bg-gradient-to-r from-sky-500 to-blue-500" isOpen={openSection === "heatmap"} onToggle={() => toggleSection("heatmap")}>
            <ActivityHeatmap heatmap={heatmap} />
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[10px] text-slate-400">Less</span>
              <div className="flex gap-[2px]">{["bg-slate-100","bg-emerald-200","bg-emerald-300","bg-emerald-400","bg-emerald-500"].map((c,i) => <div key={i} className={`w-3 h-3 rounded-sm ${c}`} />)}</div>
              <span className="text-[10px] text-slate-400">More</span>
            </div>
          </AccordionSection>
        )}

        {/* Engagement by Category */}
        {category_radar && category_radar.length > 0 && (
          <AccordionSection id="engagement" icon={BarChart3} title="Engagement by Category" count={category_radar.length} color="bg-gradient-to-r from-blue-400 to-indigo-400" isOpen={openSection === "engagement"} onToggle={() => toggleSection("engagement")}>
            <CategoryRadar data={category_radar} />
          </AccordionSection>
        )}

        {/* Savings by Category */}
        {category_savings.length > 0 && (
          <AccordionSection id="savings" icon={DollarSign} title="Savings by Category" count={category_savings.length} color="bg-gradient-to-r from-teal-400 to-teal-500" isOpen={openSection === "savings"} onToggle={() => toggleSection("savings")}>
            <div className="space-y-3">
              {category_savings.map((c, i) => {
                const cc = getCategoryColor(c.category);
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${cc.bg}`} />
                        <span className="text-sm text-slate-600">{c.category}</span>
                      </div>
                      <span className="text-sm font-bold text-green-600">${c.saved.toFixed(2)}</span>
                    </div>
                    <ProgressBar pct={(c.saved / maxCatSaved) * 100} color={cc.bg} />
                  </div>
                );
              })}
            </div>
          </AccordionSection>
        )}

        {/* Claims Over Time */}
        {Object.keys(claimed_timeline).length > 0 && (
          <AccordionSection id="timeline" icon={Gift} title="Claims Over Time" count={Object.values(claimed_timeline).reduce((a, b) => a + b, 0)} color="bg-gradient-to-r from-sky-400 to-blue-400" isOpen={openSection === "timeline"} onToggle={() => toggleSection("timeline")}>
            <div className="flex items-end justify-center gap-4 h-24">
              {Object.entries(claimed_timeline).map(([month, count], i) => {
                const maxCount = Math.max(...Object.values(claimed_timeline));
                const barH = Math.max(16, (count / maxCount) * 72);
                return (
                  <div key={i} className="flex flex-col items-center gap-1" style={{ minWidth: 36, maxWidth: 48 }}>
                    <span className="text-xs font-bold text-slate-700">{count}</span>
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: barH }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="w-8 bg-gradient-to-t from-sky-500 to-sky-400 rounded-t-lg"
                    />
                    <span className="text-[9px] text-slate-400 font-medium">{month.split(" ")[0]}</span>
                  </div>
                );
              })}
            </div>
          </AccordionSection>
        )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
